import type { DayKey } from './types';

export const pad = (n: number): string => String(n).padStart(2, '0');
export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

export const uid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().slice(0, 12)
    : Math.random().toString(36).slice(2, 11);

/** Local-time calendar key, e.g. `2026-10-14`. */
export const dayKey = (t: number | Date): DayKey => {
  const d = typeof t === 'number' ? new Date(t) : t;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Parse a DayKey as local midnight. */
export const parseDay = (k: DayKey): Date => {
  const [y, m, d] = k.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
};

/** Calendar arithmetic (DST-safe: works on dates, not 24h multiples). */
export const addDays = (k: DayKey, n: number): DayKey => {
  const d = parseDay(k);
  d.setDate(d.getDate() + n);
  return dayKey(d);
};

export const isDayKey = (k: unknown): k is DayKey =>
  typeof k === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(k) && dayKey(parseDay(k)) === k;

/** mm:ss, rounding up so a running timer never shows 00:00 early. */
export const fmtClock = (ms: number): string => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
};

export const fmtHM = (t: number): string => {
  const d = new Date(t);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const fmtDuration = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h ? `${h}H ${pad(m)}M` : `${m}M`;
};

export const num = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;
export const int = (v: unknown, fallback: number, lo: number, hi: number): number =>
  clamp(Math.round(num(v, fallback)), lo, hi);
export const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);
export const str = (v: unknown, fallback: string, max = 500): string =>
  typeof v === 'string' ? v.slice(0, max) : fallback;
export const oneOf = <T extends string>(v: unknown, options: readonly T[], fallback: T): T =>
  options.includes(v as T) ? (v as T) : fallback;
export const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
